import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import ComputerRoundedIcon from '@mui/icons-material/ComputerRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded';

import { PageHeader, PageLayout, SectionCard, LoadingState } from '../../components/ui';
import { fetchZoneSessions, fetchZoneSessionsSummary } from '../../services/zoneTrackingApi';
import api from '../../services/api';

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDatetime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleString([], {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function createDefaultFilters() {
  return {
    date_from: daysAgoIso(7),
    date_to: todayIso(),
    employee_code: '',
    project_id: '',
    status: '',
  };
}

function formatRelativeTime(value) {
  if (!value) return '-';

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return '-';

  const diffSeconds = Math.round((Date.now() - timestamp) / 1000);
  const absSeconds = Math.abs(diffSeconds);

  let unit = 's';
  let amount = absSeconds;

  if (absSeconds >= 86400) {
    unit = 'd';
    amount = Math.round(absSeconds / 86400);
  } else if (absSeconds >= 3600) {
    unit = 'h';
    amount = Math.round(absSeconds / 3600);
  } else if (absSeconds >= 60) {
    unit = 'm';
    amount = Math.round(absSeconds / 60);
  }

  if (amount === 0) return 'just now';

  return diffSeconds >= 0 ? `${amount}${unit} ago` : `in ${amount}${unit}`;
}

function getSessionStatus(row) {
  if (row?.signal_lost) return 'signal_lost';
  if (row?.left_at) return 'completed';
  return 'active';
}

function getEffectiveDurationSeconds(row) {
  if (!row?.entered_at || row?.left_at || row?.signal_lost) {
    return row?.duration_seconds ?? null;
  }

  const enteredAt = new Date(row.entered_at).getTime();
  if (Number.isNaN(enteredAt)) return null;

  return Math.max(0, Math.floor((Date.now() - enteredAt) / 1000));
}

function formatDeviceLabel(deviceType) {
  const value = String(deviceType || '').trim().toLowerCase();
  if (!value) return 'Unknown';
  if (value === 'mobile') return 'Mobile';
  if (value === 'web') return 'Web';
  return value.replace(/[_-]/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatDateRangeLabel(filters) {
  if (filters.date_from && filters.date_to && filters.date_from === filters.date_to) {
    return filters.date_from;
  }
  return `${filters.date_from || 'Any'} to ${filters.date_to || 'Any'}`;
}

function getDeviceIcon(deviceType) {
  return String(deviceType || '').trim().toLowerCase() === 'mobile'
    ? <SmartphoneRoundedIcon sx={{ fontSize: 15 }} />
    : <ComputerRoundedIcon sx={{ fontSize: 15 }} />;
}

const AUTO_REFRESH_MS = 30 * 1000;

function StatCard({ label, value, icon, color }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const palette = theme.palette[color] || theme.palette.primary;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(palette.main, 0.25),
        backgroundColor: alpha(palette.main, dark ? 0.1 : 0.06),
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 46,
          height: 46,
          borderRadius: 2,
          backgroundColor: alpha(palette.main, 0.16),
          color: palette.dark || palette.main,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem' }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: palette.dark || palette.main, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function SessionStatusChip({ isActive, signalLost }) {
  if (signalLost) {
    return (
      <Chip
        size="small"
        label="Signal Lost"
        color="warning"
        sx={{ fontWeight: 700 }}
      />
    );
  }
  if (isActive) {
    return (
      <Chip
        size="small"
        label="Active"
        icon={<RadioButtonCheckedRoundedIcon sx={{ fontSize: 12 }} />}
        color="success"
        sx={{ fontWeight: 700, animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.65 } } }}
      />
    );
  }
  return <Chip size="small" label="Completed" color="default" sx={{ fontWeight: 600 }} />;
}

function SessionMetaRow({ label, primary, secondary }) {
  return (
    <Stack direction="row" spacing={1.2} justifyContent="space-between" alignItems="flex-start">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 74, textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {primary}
        </Typography>
        {secondary ? (
          <Typography variant="caption" color="text.secondary">
            {secondary}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function SessionMobileCards({ rows }) {
  const theme = useTheme();

  return (
    <Stack spacing={1.1}>
      {rows.map((row) => {
        const status = getSessionStatus(row);
        const isActive = status === 'active';
        const signalLost = status === 'signal_lost';
        const timelineLabel = isActive ? 'Last signal' : signalLost ? 'Lost at' : 'Left';
        const timelineValue = isActive
          ? formatDatetime(row.last_signal_at || row.updated_at)
          : formatDatetime(row.left_at || row.last_signal_at);

        return (
          <Box
            key={row.id}
            sx={{
              borderRadius: 2.8,
              border: '1px solid',
              borderColor: signalLost
                ? alpha(theme.palette.warning.main, 0.35)
                : isActive
                  ? alpha(theme.palette.success.main, 0.28)
                  : theme.palette.divider,
              backgroundColor: signalLost
                ? alpha(theme.palette.warning.main, 0.05)
                : isActive
                  ? alpha(theme.palette.success.main, 0.04)
                  : alpha(theme.palette.background.paper, 0.94),
              p: 1.45,
            }}
          >
            <Stack spacing={1.2}>
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
                    {row.employee?.name || row.employee_code || 'Unknown employee'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.employee_code}
                    {row.employee?.department_name ? ` - ${row.employee.department_name}` : ''}
                  </Typography>
                </Box>
                <SessionStatusChip isActive={isActive} signalLost={signalLost} />
              </Stack>

              <Stack spacing={0.9}>
                <SessionMetaRow
                  label="Zone"
                  primary={row.project?.name || `Zone ${row.project_id}`}
                  secondary={formatDeviceLabel(row.device_type)}
                />
                <SessionMetaRow
                  label="Entered"
                  primary={formatDatetime(row.entered_at)}
                  secondary={formatRelativeTime(row.entered_at)}
                />
                <SessionMetaRow
                  label={timelineLabel}
                  primary={timelineValue}
                  secondary={isActive ? formatRelativeTime(row.last_signal_at || row.updated_at) : null}
                />
                <SessionMetaRow
                  label="Duration"
                  primary={formatDuration(getEffectiveDurationSeconds(row))}
                  secondary={
                    isActive
                      ? `Ongoing - started ${formatRelativeTime(row.entered_at)}`
                      : signalLost
                        ? 'Closed after signal loss'
                        : 'Completed session'
                  }
                />
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

export default function ZoneTrackingPage() {
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [projects, setProjects] = useState([]);

  const [filters, setFilters] = useState(createDefaultFilters);
  const [draftFilters, setDraftFilters] = useState(createDefaultFilters);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  useEffect(() => {
    api.get('/projects').then((r) => {
      setProjects(Array.isArray(r.data) ? r.data : r.data?.data ?? []);
    }).catch(() => {});
  }, []);

  const loadSummary = useCallback(async (options = {}) => {
    const { background = false } = options;
    if (!background) {
      setSummaryLoading(true);
    }
    try {
      const data = await fetchZoneSessionsSummary({
        date_from: filters.date_from,
        date_to:   filters.date_to,
      });
      setSummary(data);
    } catch {
      if (!background) {
        setSummary(null);
      }
    } finally {
      if (!background) {
        setSummaryLoading(false);
      }
    }
  }, [filters.date_from, filters.date_to]);

  const loadSessions = useCallback(async (options = {}) => {
    const { background = false } = options;
    if (!background) {
      setLoading(true);
    }
    setError(null);
    try {
      const params = {
        page:     page + 1,
        per_page: rowsPerPage,
      };
      if (filters.date_from)     params.date_from     = filters.date_from;
      if (filters.date_to)       params.date_to       = filters.date_to;
      if (filters.employee_code) params.employee_code = filters.employee_code;
      if (filters.project_id)    params.project_id    = filters.project_id;
      if (filters.status)        params.status        = filters.status;

      const data = await fetchZoneSessions(params);
      setRows(data.data ?? []);
      setTotal(data.total ?? 0);
      setLastUpdatedAt(new Date());
    } catch {
      setError('Failed to load zone sessions. Please try again.');
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }

      loadSummary({ background: true });
      loadSessions({ background: true });
    }, AUTO_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [loadSummary, loadSessions]);

  const handleDraftFilterChange = (field) => (e) => {
    setDraftFilters((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    if (draftFilters.date_from && draftFilters.date_to && draftFilters.date_from > draftFilters.date_to) {
      return;
    }

    setFilters(draftFilters);
    setPage(0);
  };

  const handleResetFilters = () => {
    const defaults = createDefaultFilters();
    setDraftFilters(defaults);
    setFilters(defaults);
    setPage(0);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    Promise.all([
      loadSummary({ background: true }),
      loadSessions({ background: true }),
    ]).finally(() => setIsRefreshing(false));
  };

  return (
    <PageLayout backgroundAccent maxWidth="xl">
      <PageHeader
        variant="dashboard"
        overline="HR Admin"
        title="Zone Time Tracking"
        subtitle="Monitor how long employees spend inside each work zone - automatically tracked when they enter or leave the geofence."
        actions={[
          <Button
            key="refresh"
            variant="outlined"
            startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />}
            onClick={handleRefresh}
          >
            {isRefreshing ? 'Refreshing' : 'Refresh'}
          </Button>,
        ]}
      />

      <Stack spacing={2.5}>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              label="Active in Zone Now"
              value={summaryLoading ? '...' : (summary?.active_now ?? 0)}
              icon={<MyLocationRoundedIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              label="Signal Lost"
              value={summaryLoading ? '...' : (summary?.signal_lost_count ?? 0)}
              icon={<WarningAmberRoundedIcon />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              label="Completed"
              value={summaryLoading ? '...' : (summary?.completed_count ?? 0)}
              icon={<TaskAltRoundedIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              label="Avg Time in Zone"
              value={summaryLoading ? '...' : formatDuration(summary?.avg_duration_seconds)}
              icon={<AccessTimeRoundedIcon />}
              color="info"
            />
          </Grid>
        </Grid>

        {summary && (
          <Grid container spacing={2}>
            {/* Top projects */}
            {summary.by_project?.length > 0 && (
              <Grid item xs={12} md={6}>
                <SectionCard
                  tone="dashboard"
                  title="Sessions by Zone"
                  subheader="Within the selected date range"
                >
                  <Stack spacing={1}>
                    {summary.by_project.map((p) => (
                      <Box
                        key={p.project_id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <MyLocationRoundedIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {p.project?.name || `Zone ${p.project_id}`}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                          <Chip size="small" label={`${p.session_count} sessions`} />
                          <Typography variant="caption" color="text.secondary">
                            avg {formatDuration(p.avg_seconds)}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </SectionCard>
              </Grid>
            )}

            {/* Top employees */}
            {summary.by_employee?.length > 0 && (
              <Grid item xs={12} md={6}>
                <SectionCard
                  tone="dashboard"
                  title="Top Employees by Time in Zone"
                  subheader="Within the selected date range"
                >
                  <Stack spacing={1}>
                    {summary.by_employee.map((e) => (
                      <Box
                        key={e.employee_code}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <PersonRoundedIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                              {e.employee?.name || e.employee_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {e.employee_code}
                            </Typography>
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                          <Chip size="small" label={`${e.session_count} sessions`} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <TrendingUpRoundedIcon sx={{ fontSize: 13 }} />
                            {formatDuration(e.total_seconds)}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </SectionCard>
              </Grid>
            )}
          </Grid>
        )}

        <SectionCard
          tone="dashboard"
          title="Zone Sessions"
          subheader="All recorded entry / exit events"
          action={
            <Tooltip title="Filters">
              <FilterListRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </Tooltip>
          }
        >
          {/* Filters */}
          <Box component="form" onSubmit={handleApplyFilters}>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Date From"
                  type="date"
                  size="small"
                  fullWidth
                  value={draftFilters.date_from}
                  onChange={handleDraftFilterChange('date_from')}
                  error={Boolean(draftFilters.date_from && draftFilters.date_to && draftFilters.date_from > draftFilters.date_to)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Date To"
                  type="date"
                  size="small"
                  fullWidth
                  value={draftFilters.date_to}
                  onChange={handleDraftFilterChange('date_to')}
                  error={Boolean(draftFilters.date_from && draftFilters.date_to && draftFilters.date_from > draftFilters.date_to)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Employee Code"
                  size="small"
                  fullWidth
                  value={draftFilters.employee_code}
                  onChange={handleDraftFilterChange('employee_code')}
                  placeholder="Exact code, e.g. 10462"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Zone / Project"
                  size="small"
                  fullWidth
                  value={draftFilters.project_id}
                  onChange={handleDraftFilterChange('project_id')}
                >
                  <MenuItem value="">All Zones</MenuItem>
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Status"
                  size="small"
                  fullWidth
                  value={draftFilters.status}
                  onChange={handleDraftFilterChange('status')}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="signal_lost">Signal Lost</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.2}
              justifyContent="space-between"
              alignItems={{ md: 'center' }}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  { label: 'Today', from: todayIso(), to: todayIso() },
                  { label: '3 days', from: daysAgoIso(2), to: todayIso() },
                  { label: '7 days', from: daysAgoIso(7), to: todayIso() },
                  { label: '30 days', from: daysAgoIso(30), to: todayIso() },
                ].map((preset) => (
                  <Chip
                    key={preset.label}
                    label={preset.label}
                    variant={
                      filters.date_from === preset.from && filters.date_to === preset.to
                        ? 'filled'
                        : 'outlined'
                    }
                    color={
                      filters.date_from === preset.from && filters.date_to === preset.to
                        ? 'primary'
                        : 'default'
                    }
                    onClick={() => {
                      setDraftFilters((current) => ({
                        ...current,
                        date_from: preset.from,
                        date_to: preset.to,
                      }));
                      setFilters((current) => ({
                        ...current,
                        date_from: preset.from,
                        date_to: preset.to,
                      }));
                      setPage(0);
                    }}
                  />
                ))}
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    (draftFilters.date_from && draftFilters.date_to && draftFilters.date_from > draftFilters.date_to) ||
                    JSON.stringify(draftFilters) === JSON.stringify(filters)
                  }
                >
                  Apply filters
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip
              size="small"
              variant="outlined"
              icon={<CalendarTodayRoundedIcon sx={{ fontSize: 14 }} />}
              label={`Range: ${formatDateRangeLabel(filters)}`}
            />
            <Chip size="small" variant="outlined" label={`Showing ${rows.length} of ${total}`} />
            {summary?.total_sessions !== undefined ? (
              <Chip size="small" variant="outlined" label={`${summary.total_sessions} sessions in range`} />
            ) : null}
            {lastUpdatedAt ? (
              <Chip size="small" variant="outlined" label={`Updated ${formatRelativeTime(lastUpdatedAt)}`} />
            ) : null}
          </Stack>

          {/* Table */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
          )}

          {loading ? (
            <LoadingState rows={6} header={false} />
          ) : rows.length === 0 ? (
            <Box
              sx={{
                py: 6,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              <MyLocationRoundedIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              <Typography variant="body2">No zone sessions found for the selected filters.</Typography>
              <Button
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={handleResetFilters}
              >
                Reset filters
              </Button>
            </Box>
          ) : (
            <>
              {downMd ? (
                <SessionMobileCards rows={rows} />
              ) : (
              <TableContainer
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: 760,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Employee', 'Code', 'Zone', 'Entered', 'Left', 'Duration', 'Status'].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: 'text.secondary',
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            whiteSpace: 'nowrap',
                            backgroundColor: alpha(theme.palette.background.paper, 0.98),
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const status = getSessionStatus(row);
                      const isActive = status === 'active';
                      const signalLost = status === 'signal_lost';
                      const timelineLabel = isActive ? 'Last signal' : signalLost ? 'Lost at' : 'Left';
                      const timelineValue = isActive
                        ? formatDatetime(row.last_signal_at || row.updated_at)
                        : formatDatetime(row.left_at || row.last_signal_at);
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            backgroundColor: signalLost
                              ? alpha(theme.palette.warning.main, 0.04)
                              : isActive
                                ? alpha(theme.palette.success.main, 0.04)
                                : 'transparent',
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.employee?.name || '-'}
                            </Typography>
                            {row.employee?.department_name ? (
                              <Typography variant="caption" color="text.secondary">
                                {row.employee.department_name}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {row.employee_code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MyLocationRoundedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                              <Typography variant="body2">
                                {row.project?.name || `Zone ${row.project_id}`}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={getDeviceIcon(row.device_type)}
                              label={formatDeviceLabel(row.device_type)}
                              sx={{ mt: 0.75 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {formatDatetime(row.entered_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color={isActive ? 'text.disabled' : 'text.primary'} sx={{ whiteSpace: 'nowrap' }}>
                              {timelineValue}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {timelineLabel}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isActive && (
                                <CircularProgress size={11} thickness={5} color="success" />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: signalLost ? 'warning.dark' : isActive ? 'success.dark' : 'text.primary',
                                }}
                              >
                                {formatDuration(getEffectiveDurationSeconds(row))}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {isActive
                                ? `Ongoing - started ${formatRelativeTime(row.entered_at)}`
                                : signalLost
                                  ? 'Closed after signal loss'
                                  : `Ended ${formatRelativeTime(row.left_at)}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <SessionStatusChip isActive={isActive} signalLost={signalLost} />
                            {isActive ? (
                              <Typography variant="caption" color="text.secondary">
                                Last signal {formatRelativeTime(row.last_signal_at || row.updated_at)}
                              </Typography>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </SectionCard>

      </Stack>
    </PageLayout>
  );
}


